import { AbilityTuple, defineAbility, MongoAbility } from "@casl/ability";
import { createContext, ReactNode } from "react";
import { AuthUser } from "../@type/auth";
import useAuth from "../hooks/useAuth";

type ApiActionType = 'manage' | 'access';
type ApiSubjectType = 'api' | 'all';
export type ApiTupel = AbilityTuple<ApiActionType, ApiSubjectType>;

type BrokerHubActionType = 'manage' | 'access';
type BrokerHubSubjectType = 'broker-hub' | 'all';
export type BrokerHubTupel = AbilityTuple<BrokerHubActionType, BrokerHubSubjectType>;

type PhysicalMarketsActionType = 'manage' | 'access' | 'read' | 'create' | 'update' | 'delete' | 'import' | 'export' | 'send';
type PhysicalMarketsSubjectType = 'physical-markets' | 'all' | 'periods' | 'dailypricings' | 'data' | 'report';
export type PhysicalMarketsTupel = AbilityTuple<PhysicalMarketsActionType, PhysicalMarketsSubjectType>;

type RiskManagementActionType = 'manage'|'access'|'create'|'update'|'delete'|'read'|'sendReport';
export type RiskManagementReportSubjectType = 'risk-management';
type RiskManagementSubjectType = RiskManagementReportSubjectType|'all'|'desks'|'portfolios'|'portfolioKeyLimitAlerts'|'reportSetting';
export type RiskManagementTupel = AbilityTuple<RiskManagementActionType, RiskManagementSubjectType>;

type ManagementActionType = 'manage' | 'access' | 'create' | 'update' | 'delete';
type ManagementType = 'management' | 'all' | 'users';
export type ManagementTupel = AbilityTuple<ManagementActionType, ManagementType>;

const ApiAbilityContext = createContext<MongoAbility<ApiTupel> | null>(null);
const BrokerHubAbilityContext = createContext<MongoAbility<BrokerHubTupel> | null>(null);
const PhysicalMarketsAbilityContext = createContext<MongoAbility<PhysicalMarketsTupel> | null>(null);
const RiskManagementAbilityContext = createContext<MongoAbility<RiskManagementTupel>|null>(null);
const ManagementAbilityContext = createContext<MongoAbility<ManagementTupel> | null>(null);

function defineApiAbilityFor(user: AuthUser) {
    return defineAbility<MongoAbility<ApiTupel>>((can, cannot) => {
        const role = user?.Applications?.find(e => e.ApplicationCode === 'API')?.Role;
        if (role) {
            if (role === 'ADMIN')
                can('manage', 'all');
            else
                cannot('access', 'api');
        }
        else
            cannot('access', 'api');
    });
}

function defineBrokerHubAbilityFor(user: AuthUser) {
    return defineAbility<MongoAbility<BrokerHubTupel>>((can, cannot) => {
        const role = user?.Applications?.find(e => e.ApplicationCode === 'BROKER-HUB')?.Role;
        if (role) {
            if (role === 'ADMIN')
                can('manage', 'all');
            else
                cannot('access', 'broker-hub');
        }
        else
            cannot('access', 'broker-hub');
    });
}

function definePhyscalMarketsAbilityFor(user: AuthUser) {
    return defineAbility<MongoAbility<PhysicalMarketsTupel>>((can, cannot) => {
        const role = user?.Applications?.find(e => e.ApplicationCode === 'PHYSICAL-MARKETS')?.Role;
        if (role) {
            if (role === 'ADMIN')
                can('manage', 'all');
            else if (role === 'TRADER') {
                can('manage', 'periods');
                can('manage', 'dailypricings');
                can('import', 'data');
                can('export', 'data');
                can('send', 'report');
                can('access', 'all');
            }
            else if (role === 'VIEWER') {
                can('read', 'periods');
                can('read', 'dailypricings');
                cannot('import', 'data');
                cannot('export', 'data');
                can('access', 'all');
            }
            else
                cannot('access', 'physical-markets');
        }
        else
            cannot('access', 'physical-markets');
    });
}

function defineRiskManagementAbilityFor(user: AuthUser){
    return defineAbility<MongoAbility<RiskManagementTupel>>((can, cannot) => {
        const role = user?.Applications?.find(e => e.ApplicationCode === 'RISK-MANAGEMENT')?.Role;
        if (role) {
            if (role === 'ADMIN')
                can('manage', 'all');
            else if(role === 'VIEWER')
            {
                can('access', 'risk-management');
            }
            else
                cannot('access', 'risk-management');
        }
        else
            cannot('access', 'risk-management');
    });
}

function definedManagementAbilityFor(user: AuthUser) {
    return defineAbility<MongoAbility<ManagementTupel>>((can, cannot) => {
        const role = user?.Applications?.find(e => e.ApplicationCode === 'MANAGEMENT')?.Role;
        if (role) {
            if (role === 'ADMIN')
                can('manage', 'all');
            else
                cannot('access', 'management');
        }
        else
            cannot('access', 'management');
    });
}

type AccessProviderProps = {
    children: ReactNode;
};

function AccessProvider({ children }: AccessProviderProps) {
    const { user } = useAuth();

    return (<>
        <ApiAbilityContext.Provider value={defineApiAbilityFor(user)}>
            <BrokerHubAbilityContext.Provider value={defineBrokerHubAbilityFor(user)}>
                <PhysicalMarketsAbilityContext.Provider value={definePhyscalMarketsAbilityFor(user)}>
                    <RiskManagementAbilityContext.Provider value={defineRiskManagementAbilityFor(user)}>
                        <ManagementAbilityContext.Provider value={definedManagementAbilityFor(user)}>
                            {children}
                        </ManagementAbilityContext.Provider>
                    </RiskManagementAbilityContext.Provider>
                </PhysicalMarketsAbilityContext.Provider>
            </BrokerHubAbilityContext.Provider>
        </ApiAbilityContext.Provider>
    </>);
}

export { AccessProvider, ApiAbilityContext, BrokerHubAbilityContext, PhysicalMarketsAbilityContext, RiskManagementAbilityContext, ManagementAbilityContext };
